// src/components/layout/Header.jsx
import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  MessageSquare,
  Menu,
  X,
  ChevronDown,
  User,
  LogOut,
  Settings,
  Award,
  BookOpen,
  Sparkles,
  Home,
  Users,
  Compass
} from 'lucide-react';
import Button from '../shared/Button';
import { useAuth } from '../../context/AuthContext';
import { useApp } from '../../context/AppContext';
import { noteCompanionEntry } from '../../services/coachModuleContext';

const Header = ({ scrolled, mobileMenuOpen, setMobileMenuOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logOut, redirectToAuth } = useAuth();
  const { assessmentResults } = useApp();

  const navigation = [
    { name: 'Assessment', href: '/assessment', icon: Compass },
    { name: 'Dive In', href: '/modules', icon: BookOpen },
    { name: 'Coach', href: '/coach', icon: Sparkles },
    { name: 'Toolkit', href: '/toolkit', icon: Users },
    { name: 'Dashboard', href: '/dashboard', icon: Award },
    { name: 'Resources', href: '/resources', icon: MessageSquare },
  ];

  const handleLogout = async () => {
    try {
      if (await logOut()) return; // on the way to the hub's sign-out
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled ? 'bg-slate-800/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
    }`}>
      <nav className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src="/relationship-compass-logo.png"
              alt="Relationship Compass"
              className="w-16 h-16 object-contain group-hover:scale-110 transition-transform flex-shrink-0"
            />
            <div className="hidden sm:flex flex-col">
              <h1 className="text-xl font-bold text-white group-hover:text-rose-400 transition-colors" style={{ lineHeight: '1.2' }}>
                Relationship Compass
              </h1>
              <p className="text-xs text-slate-400 group-hover:text-rose-300 transition-colors" style={{ lineHeight: '1', marginTop: '2px' }}>A Dive Deep Dialogues App</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1 xl:gap-3">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={item.href === '/coach' ? noteCompanionEntry : undefined}
                  className={`flex items-center gap-2 px-2 xl:px-3 py-2 rounded-lg whitespace-nowrap transition-all ${
                    isActive
                      ? 'bg-rose-500/20 text-rose-400'
                      : 'text-slate-300 hover:text-rose-400 hover:bg-slate-800/20'
                  }`}
                >
                  <Icon className="w-4 h-4" aria-hidden="true" />
                  <span className="font-medium">{item.name}</span>
                </Link>
              );
            })}
          </div>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center gap-2 xl:gap-4">
            {user ? (
              <>
                {/* User Progress Badge */}
                {assessmentResults && (
                  <div className="hidden xl:flex items-center gap-2 px-3 py-1 bg-green-500/20 rounded-full">
                    <Award className="w-4 h-4 text-green-400" aria-hidden="true" />
                    <span className="text-sm text-green-300">
                      {assessmentResults.archetype || 'Explorer'}
                    </span>
                  </div>
                )}

                {/* Settings Icon */}
                <Link
                  to="/privacy?tab=settings"
                  className="p-2 rounded-lg hover:bg-slate-800/20 transition-colors"
                  aria-label="Settings"
                  title="Settings"
                >
                  <Settings className="w-5 h-5 text-slate-400 hover:text-rose-400 transition-colors" aria-hidden="true" />
                </Link>

                {/* Logout Button */}
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 px-2 xl:px-4 py-2 rounded-lg whitespace-nowrap hover:bg-slate-800/20 transition-colors"
                  aria-label="Log Out"
                  title="Log Out"
                >
                  <LogOut className="w-5 h-5 text-slate-400" aria-hidden="true" />
                  <span className="hidden xl:inline text-slate-300 hover:text-rose-400 transition-colors">Log Out</span>
                </button>
              </>
            ) : (
              <Button
                onClick={() => redirectToAuth('signin')}
                variant="secondary"
              >
                Log In
              </Button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-white/10 transition-colors"
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6 text-white" aria-hidden="true" />
            ) : (
              <Menu className="w-6 h-6 text-white" aria-hidden="true" />
            )}
          </button>
        </div>
      </nav>
    </header>
  );
};

export default Header;